<template>
  <div class="page-admin-opportunity-labels">
    <BaseLoadingBar is-small v-if="pending" />
    <div class="page-admin-opportunity-labels__body">
      <!-- Phones: its main action, as the "+" of the settings header. -->
      <PageMainAction :label="$t('page.admin-opportunity-labels.add-label')" @click="openAddLabel" />
      <div v-if="!isPhone" class="page-admin-opportunity-labels__table-header-actions">
        <div class="page-admin__count">
          <span class="number-monospace">{{ opportunityLabels.length }}</span>
          {{ ' ' + $tc('page.admin-opportunity-labels.label', opportunityLabels.length) }}
        </div>
        <div class="page-admin__actions">
          <BaseRefreshButton :size="40" :pending="pending" @refresh="onRefresh" />
          <BaseButton @click.native="openAddLabel" has-icon-left>
            <BaseIcon name="plus" />
            {{ $t('page.admin-opportunity-labels.add-label') }}
          </BaseButton>
        </div>
      </div>
      <div class="page-admin-opportunity-labels__table">
        <TableNormal
          :headers="headers"
          :items="opportunityLabels"
          :items-per-page="itemsToShow"
          :fixed-header="true"
          :single-select="false"
          hide-default-footer
          item-key="id"
          :mobile-breakpoint="620"
          @click:row="openEditLabel"
        >
          <!-- Phones: the table draws the count line above its rows (docs/page-layout.md). -->
          <template #phone-count>
            <span class="number-monospace">{{ opportunityLabels.length }}</span>
            {{ ' ' + $tc('page.admin-opportunity-labels.label', opportunityLabels.length) }}
          </template>
          <template #phone-actions>
            <BaseRefreshButton :pending="pending" @refresh="onRefresh" />
          </template>
          <template v-slot:item.name="{ item }">
            <BaseTag :background="item.color">{{ item.name }}</BaseTag>
          </template>
          <template v-slot:item.actions="{ item }">
            <BaseMoreActions
              v-if="isPhone"
              :title="item.name"
              :actions="rowActions(item)"
              @select="onRowAction($event, item)"
            />
            <v-menu v-else>
              <template v-slot:activator="{ on, attrs }">
                <div @click.stop v-on="on" v-bind="attrs" class="page-admin-opportunity-labels__menu-activator">
                  <BaseIcon name="more-horizontal" class="page-admin-opportunity-labels__more" />
                </div>
              </template>
              <div class="page-admin-opportunity-labels__actions-menu base-actions-menu">
                <ul>
                  <li class="edit" @click="openEditLabel(item)">
                    <BaseIcon :size="44" name="edit-2" />
                    {{ $t('actions.edit') }}
                  </li>
                  <li class="delete" @click="openDeleteLabel(item)">
                    <BaseIcon :size="44" name="trash-2" />
                    {{ $t('actions.delete') }}
                  </li>
                </ul>
              </div>
            </v-menu>
          </template>
        </TableNormal>
      </div>
    </div>
    <AddOpportunityLabelDialog ref="addOpportunityLabelDialog" @action-succeeded="onRefresh" />
    <EditOpportunityLabelDialog ref="editOpportunityLabelDialog" @action-succeeded="onRefresh" />
    <DeleteOpportunityLabelDialog ref="deleteOpportunityLabelDialog" @action-succeeded="onRefresh" />
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import TableNormal from '@/components/table/TableNormal.vue';
import PageMainAction from '@/components/layout/PageMainAction.vue';
import { viewport } from '@/util/viewport';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import { mapActions, mapGetters } from 'vuex';
import AddOpportunityLabelDialog from '@/components/page-admin-opportunity-labels/AddOpportunityLabelDialog.vue';
import EditOpportunityLabelDialog from '@/components/page-admin-opportunity-labels/EditOpportunityLabelDialog.vue';
import DeleteOpportunityLabelDialog from '@/components/page-admin-opportunity-labels/DeleteOpportunityLabelDialog.vue';

export default {
  name: 'PageAdminOpportunityLabels',
  components: {
    BaseMoreActions,
    BaseRefreshButton,
    PageMainAction,
    DeleteOpportunityLabelDialog,
    BaseLoadingBar,
    AddOpportunityLabelDialog,
    EditOpportunityLabelDialog,
    BaseTag,
    TableNormal,
    BaseButton,
    BaseIcon,
  },
  data() {
    return {
      itemsToShow: 100,
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    ...mapGetters('opportunityLabels', ['opportunityLabels', 'pending', 'error']),
    headers() {
      return [
        {
          text: this.$tc('page.admin-opportunity-labels.label'),
          sortable: false,
          value: 'name',
          width: '25%',
        },
        {
          text: this.$t('page.admin-opportunity-labels.description'),
          sortable: false,
          value: 'description',
          phone: 'subtitle',
        },
        {
          text: '',
          sortable: false,
          align: 'right',
          value: 'actions',
          width: '1%',
        },
      ];
    },
  },
  methods: {
    // The row's ⋯ on phones, a sheet (BaseMoreActions); the desktop's menu lists the same.
    rowActions(item) {
      return [
        { key: 'edit', label: this.$t('actions.edit'), icon: 'edit-2' },
        { key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true },
      ];
    },
    onRowAction(key, item) {
      if (key === 'edit') this.openEditLabel(item);
      else this.openDeleteLabel(item);
    },
    ...mapActions('opportunityLabels', ['getOpportunityLabels']),
    openAddLabel() {
      this.$refs.addOpportunityLabelDialog.openDialog();
    },
    openEditLabel(label) {
      this.$refs.editOpportunityLabelDialog.openDialog(label);
    },
    openDeleteLabel(label) {
      this.$refs.deleteOpportunityLabelDialog.openDialog(label);
    },
    onRefresh() {
      this.getOpportunityLabels();
    },
  },
  async created() {
    this.getOpportunityLabels();
  },
};
</script>

<style lang="scss">
.page-admin-opportunity-labels {
  &__body {
    display: flex;
    flex: auto;
    flex-direction: column;
    padding: 32px;
  }

  &__table-header-actions {
    padding-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__menu-activator {
    display: inline-flex;
  }

  &__more {
    cursor: pointer;
    margin-right: 24px;
  }

  &__table {
    margin-bottom: 32px;
  }

  &__actions-menu {
    min-width: 140px;
    cursor: pointer;
  }
}
</style>
